/**
 * QR Login Landing Page（公開ルート `/qr-login`）
 * Task 7.2: 公開ランディング画面と自動ログイン遷移
 * Requirements: 4.1, 5.1, 5.5, 5.6, 5.7
 *
 * 認証ガード外の公開ルートとして、URL クエリ `t`（生トークン）を抽出し、
 * 未認証の消費エンドポイント（POST /auth/qr-login）を呼び出す。消費成功後は
 * AuthContext.qrLogin に橋渡しして自動ログインし、完了で /dashboard へ遷移する。
 *
 * 状態遷移: loading（消費中）→ authenticating（Custom Auth 中, 5.5）→ done / error。
 * エラーは理由（invalid / used / expired, 5.7）別の文言で案内する。
 * トークン消費後は history.replaceState で URL から `t` を除去する（セキュリティ）。
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import {
  consumeQrLogin,
  resolveQrLoginReason,
  type QrLoginReason,
} from '../../admin/api/qrAuthApi';
import { useAuth } from '../context/AuthContext';

type LandingStatus = 'loading' | 'authenticating' | 'done' | 'error';

/** 失敗理由ごとの UX 文言（Requirement 5.7）。 */
const REASON_MESSAGES: Record<QrLoginReason, string> = {
  invalid: 'この QR コードは無効です。管理者に新しい QR コードの発行を依頼してください。',
  used: 'この QR コードは既に使用されています。管理者に新しい QR コードの発行を依頼してください。',
  expired:
    'この QR コードは有効期限が切れています。管理者に新しい QR コードの発行を依頼してください。',
};

const GENERIC_ERROR_MESSAGE =
  'ログイン処理中にエラーが発生しました。時間をおいて再度お試しください。';
const MISSING_TOKEN_MESSAGE = 'ログイン情報が見つかりません。QR コードを再度読み取ってください。';

export const QrLoginLandingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { qrLogin } = useAuth();

  const [status, setStatus] = useState<LandingStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>(GENERIC_ERROR_MESSAGE);

  // React 18 StrictMode の二重実行や再レンダーでの多重消費を防止する。
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = searchParams.get('t');

    // 生トークンが無い/空の場合はエラー状態（消費は行わない）。
    if (!token) {
      setErrorMessage(MISSING_TOKEN_MESSAGE);
      setStatus('error');
      return;
    }

    const run = async () => {
      try {
        // 1) 未認証で生トークンを消費する（loading）。
        const result = await consumeQrLogin(token);

        // 2) 消費後、URL クエリから生トークンを除去する（セキュリティ）。
        removeTokenFromUrl();

        // 3) Custom Auth / mock 適用へ橋渡しする（authenticating, Requirement 5.5）。
        setStatus('authenticating');

        if ('mock' in result) {
          // ローカル: mock トークンを直接適用（決定 D5）。
          await qrLogin({ mode: 'mock', tokens: result.tokens });
        } else {
          // 本番: Cognito Custom Auth を実行。
          await qrLogin({
            mode: 'cognito',
            username: result.username,
            challengeSecret: result.challengeSecret,
          });
        }

        // 4) 完了（done）→ ログイン後画面へ遷移。
        setStatus('done');
        navigate('/dashboard', { replace: true });
      } catch (error) {
        // 消費に失敗した場合も URL からトークンを除去する。
        removeTokenFromUrl();
        // 消費エラー（axios）の場合は理由別文言、Custom Auth 等のその他は汎用文言。
        if (isConsumeError(error)) {
          const reason = resolveQrLoginReason(error);
          setErrorMessage(REASON_MESSAGES[reason]);
        } else {
          setErrorMessage(GENERIC_ERROR_MESSAGE);
        }
        setStatus('error');
      }
    };

    void run();
    // searchParams / navigate / qrLogin は初回マウント時の値で実行（startedRef でガード）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        {(status === 'loading' || status === 'authenticating' || status === 'done') && (
          <Stack spacing={3} alignItems="center">
            <CircularProgress />
            <Typography variant="h6">
              {status === 'loading' && 'QR コードを確認しています…'}
              {status === 'authenticating' && 'ログイン処理中です…'}
              {status === 'done' && 'ログインしました。画面を移動します…'}
            </Typography>
            {status !== 'done' && (
              <Typography variant="body2" color="text.secondary">
                このページを閉じずにお待ちください。
              </Typography>
            )}
          </Stack>
        )}

        {status === 'error' && (
          <Stack spacing={3} alignItems="center">
            <Typography variant="h6" color="error">
              ログインできませんでした
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {errorMessage}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
              ログイン画面へ
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

/**
 * URL クエリから生トークン `t` を除去する（履歴に残さない / セキュリティ）。
 * SPA のルーティングには影響させず history.replaceState のみで書き換える。
 */
function removeTokenFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('t')) {
      url.searchParams.delete('t');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // URL 操作に失敗しても致命的ではないため無視する。
  }
}

/**
 * 消費エンドポイント由来のエラー（理由を持つ可能性がある）かを判定する。
 * axios エラーは消費呼び出しに由来するため理由別文言の対象とする。
 */
function isConsumeError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: boolean }).isAxiosError === true
  );
}
